import { useCallback, useEffect, useRef, useState } from 'react';

import { createWedgeBuffer, isTextEntryElement, PRESENCE_TIMEOUT_MS } from './wedge';

/**
 * Staff-web adaptation of the portal's card reader hook
 * (apps/portal-web/src/features/auth/useCardReader.ts). Apps never import
 * from each other, so this is a copy — with two deliberate differences:
 *
 *  - burst assembly lives in `./wedge` so the timing rules are unit-testable
 *    in plain node;
 *  - the caller arms it explicitly (the Tap-to-find panel is focused), because
 *    the staff screen carries live forms the reader must never fight with.
 *
 * The hold-time semantics are the portal's, unchanged: repeats refresh
 * presence and complete the hold on their own; silence moves to `lapsed`,
 * which asks for the card again or an explicit confirm; a zero hold means
 * read-and-go. The Cards page passes `holdSeconds: 0` — the portal's hold
 * exists so a card in a back pocket cannot sign someone in and start charging
 * them, whereas here the tap is the staff member's own deliberate act and
 * resolves a lookup, not a billing session.
 */

export type CardReaderPhase = 'idle' | 'holding' | 'lapsed' | 'submitting';

const TICK_MS = 100;

interface CardReaderOptions {
  enabled: boolean;
  holdSeconds: number;
  onRead: (cardId: string) => void | Promise<void>;
}

export interface CardReaderState {
  phase: CardReaderPhase;
  /** 0..1, drives the indicator that fills while the card is held. */
  progress: number;
  cancel: () => void;
  /** Accept a card that was read but stopped repeating. */
  confirm: () => void;
}

export function useCardReader({
  enabled,
  holdSeconds,
  onRead,
}: CardReaderOptions): CardReaderState {
  const [phase, setPhase] = useState<CardReaderPhase>('idle');
  const [progress, setProgress] = useState(0);

  const bufferRef = useRef(createWedgeBuffer());
  const cardIdRef = useRef<string | null>(null);
  const holdStartedAtRef = useRef(0);
  const lastSeenAtRef = useRef(0);

  /**
   * The phase, readable from the tick without making it a dependency.
   *
   * The interval has to branch on the current phase and then change it. Doing
   * that inside a `setPhase` updater would put a side effect in a function
   * React is free to call twice (it does, under StrictMode), so the phase is
   * mirrored here and the updater stays pure.
   */
  const phaseRef = useRef<CardReaderPhase>('idle');
  const setPhaseNow = useCallback((next: CardReaderPhase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  // Kept in a ref so the keyboard listener is attached once rather than being
  // torn down and rebuilt every time the parent re-renders mid-read.
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;

  const holdMs = Math.max(0, holdSeconds) * 1000;

  const reset = useCallback(() => {
    bufferRef.current.reset();
    cardIdRef.current = null;
    holdStartedAtRef.current = 0;
    lastSeenAtRef.current = 0;
    setProgress(0);
    setPhaseNow('idle');
  }, [setPhaseNow]);

  const submit = useCallback(
    (cardId: string) => {
      setPhaseNow('submitting');
      setProgress(1);
      void Promise.resolve(onReadRef.current(cardId)).finally(() => {
        bufferRef.current.reset();
        cardIdRef.current = null;
        setProgress(0);
        setPhaseNow('idle');
      });
    },
    [setPhaseNow],
  );

  const commit = useCallback(
    (cardId: string) => {
      const now = Date.now();

      // The same card still on the reader: refresh presence, keep the hold.
      if (
        cardIdRef.current === cardId &&
        (phaseRef.current === 'holding' || phaseRef.current === 'lapsed')
      ) {
        lastSeenAtRef.current = now;
        setPhaseNow('holding');
        return;
      }

      cardIdRef.current = cardId;
      holdStartedAtRef.current = now;
      lastSeenAtRef.current = now;
      setProgress(0);
      setPhaseNow('holding');

      // Zero hold gets the plain behaviour: read and go.
      if (holdMs === 0) submit(cardId);
    },
    [holdMs, setPhaseNow, submit],
  );

  useEffect(() => {
    if (!enabled) return;

    function handleKeyDown(event: KeyboardEvent) {
      // The wedge types into whatever has focus. While staff are typing in
      // the assign form those keystrokes are theirs, so the reader is ignored
      // there — the Tap panel is where a card gets read.
      const target = event.target;
      if (target instanceof HTMLElement && isTextEntryElement(target)) return;

      const value = bufferRef.current.feed(event.key, Date.now());
      if (value !== null) commit(value);
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, commit]);

  // Everything time-based runs off one tick: the hold's progress, the
  // no-terminator commit, and the presence timeout.
  useEffect(() => {
    if (!enabled) return;

    const timer = setInterval(() => {
      const now = Date.now();

      // Reader that sends no Enter: the burst has stopped, take what we have.
      const flushed = bufferRef.current.flushIfIdle(now);
      if (flushed !== null) {
        commit(flushed);
        return;
      }

      const cardId = cardIdRef.current;
      if (!cardId || holdMs === 0 || phaseRef.current !== 'holding') return;

      const elapsed = now - holdStartedAtRef.current;
      setProgress(Math.min(1, elapsed / holdMs));

      if (elapsed >= holdMs) {
        submit(cardId);
      } else if (now - lastSeenAtRef.current > PRESENCE_TIMEOUT_MS) {
        setPhaseNow('lapsed');
      }
    }, TICK_MS);

    return () => clearInterval(timer);
  }, [enabled, holdMs, commit, submit, setPhaseNow]);

  useEffect(() => {
    if (!enabled) reset();
  }, [enabled, reset]);

  const confirm = useCallback(() => {
    const cardId = cardIdRef.current;
    if (cardId) submit(cardId);
  }, [submit]);

  return { phase, progress, cancel: reset, confirm };
}
